import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Users, Star, Swords, BadgeCheck, ArrowRight, Radio, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PublicPageShell } from "@/components/landing/PublicPageShell";
import { MOCK_CHALLENGES, CATEGORY_ICON, CATEGORY_COLOR } from "@/data/marketplaceMockData";

const REAL_TRACK_IDS = ["data_cloud", "sfmc", "mc_next", "admin_dev", "servicenow"];

const MOCK_DAY_TEMPLATES = [
  "Kickoff & Environment Setup", "Core Fundamentals — Part 1", "Core Fundamentals — Part 2", "Hands-On Lab #1",
  "Boss Battle — Checkpoint 1", "Intermediate Concepts", "Tooling Deep-Dive", "Real-World Case Study #1",
  "Building Your First Project", "Boss Battle — Checkpoint 2", "Advanced Patterns", "Performance & Best Practices",
  "Testing & Debugging Mastery", "Real-World Case Study #2", "Boss Battle — Checkpoint 3", "Security & Reliability",
  "Scaling for Production", "Integrations & APIs", "Team Workflows & Collaboration", "Boss Battle — Checkpoint 4",
  "Capstone Project Kickoff", "Capstone Build — Sprint 1", "Capstone Build — Sprint 2", "Capstone Polish & Review",
  "Boss Battle — Final Challenge", "Interview Prep & Mock Round", "Portfolio & Case Study Writeup",
  "Peer Review & Feedback", "Certification Readiness Review", "Graduation & Certificate",
];

/** One shared, consistent preview design for EVERY challenge card in the Marketplace —
 * the 5 real built-in tracks (real curriculum from GET /api/public/challenge-preview/:track)
 * AND the illustrative community-style cards (client-side MOCK_CHALLENGES). Clicking any
 * card in ChallengeMarketplaceSection now opens this page instead of jumping straight to
 * /register, so a visitor sees exactly what they're signing up for first. */
export default function ChallengePreviewPage() {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    if (REAL_TRACK_IDS.includes(challengeId)) {
      api.get(`/public/challenge-preview/${challengeId}`).then((r) => {
        setPreview({ ...r.data, isReal: true, isCustomReal: false, id: challengeId });
      }).finally(() => setLoading(false));
    } else {
      const mock = MOCK_CHALLENGES.find((c) => c.id === challengeId);
      api.get(`/custom-challenges/${challengeId}/preview`).then((r) => {
        setPreview({
          ...mock, title: r.data.title, category: r.data.category, description: r.data.description,
          day_titles: r.data.day_titles, total_days: r.data.total_days,
          isReal: true, isCustomReal: true, id: challengeId,
        });
      }).catch(() => {
        if (mock) {
          setPreview({
            ...mock, isReal: false, isCustomReal: false, description: `Join ${mock.learners.toLocaleString()} learners mastering ${mock.title} through daily hands-on lessons, quizzes, and a Boss Battle.`,
            day_titles: Array.from({ length: mock.durationDays }, (_, i) => MOCK_DAY_TEMPLATES[i] || `${mock.title}: Day ${i + 1}`),
            total_days: mock.durationDays,
          });
        }
      }).finally(() => setLoading(false));
    }
  }, [challengeId]);

  const handleStart = () => {
    if (preview.isCustomReal) {
      if (user) {
        navigate(`/custom-challenges/${preview.id}`);
      } else {
        sessionStorage.setItem("pending_join_challenge_id", preview.id);
        navigate("/register");
      }
      return;
    }
    if (!preview.isReal) {
      navigate("/register");
      return;
    }
    navigate(user ? preview.start_route : "/register");
  };

  if (loading) {
    return <PublicPageShell><div className="h-96" /></PublicPageShell>;
  }

  if (!preview) {
    return (
      <PublicPageShell>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="challenge-preview-not-found">
          <p className="text-slate-300">We couldn't find that challenge.</p>
          <Link to="/marketplace" className="text-indigo-400 hover:text-indigo-300 text-sm">Back to Marketplace</Link>
        </div>
      </PublicPageShell>
    );
  }

  const accent = CATEGORY_COLOR[preview.category] || "#5C9DFF";
  const Icon = CATEGORY_ICON[preview.category] || Swords;

  return (
    <PublicPageShell>
      <div className="max-w-4xl mx-auto px-6 py-10" data-testid="challenge-preview-page">
        <button onClick={() => navigate("/marketplace")} data-testid="challenge-preview-back-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Marketplace
        </button>

        <div className="rounded-3xl border border-white/10 bg-[#0A101D]/70 backdrop-blur-xl p-6 sm:p-8 mb-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `radial-gradient(circle at 15% 0%, ${accent}25, transparent 55%)` }} />
          <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}18`, border: `1px solid ${accent}30` }}>
                  <Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={1.5} />
                </div>
                {preview.isReal && !preview.isCustomReal && (
                  <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-400/15 border border-emerald-400/30 rounded-full px-2.5 py-1">
                    <Radio className="w-2.5 h-2.5" /> Live Platform Track
                  </span>
                )}
                {preview.isCustomReal && (
                  <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B58BFF] bg-[#B58BFF]/15 border border-[#B58BFF]/30 rounded-full px-2.5 py-1">
                    <Radio className="w-2.5 h-2.5" /> AI-Generated Curriculum
                  </span>
                )}
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: accent }}>{preview.category}</span>
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white mt-1" data-testid="challenge-preview-title">{preview.title}</h1>
              <p className="text-sm text-slate-400 mt-2.5 max-w-xl leading-relaxed">{preview.description}</p>
              <div className="flex items-center gap-4 mt-4 text-xs text-slate-400 flex-wrap">
                {preview.creatorName && <span>By {preview.creatorName}{preview.creatorVerified && <BadgeCheck className="w-3.5 h-3.5 text-[#5C9DFF] inline ml-1 -mt-0.5" />}</span>}
                {preview.learners !== undefined && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {preview.learners.toLocaleString()} learners</span>}
                {preview.rating && <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-amber-400" /> {preview.rating}</span>}
                <span>{preview.total_days} Days</span>
              </div>
              {preview.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {preview.skills.map((s) => (
                    <span key={s} className="text-[11px] text-slate-300 bg-white/[0.04] border border-white/10 rounded-full px-2.5 py-1">{s}</span>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={handleStart} data-testid="challenge-preview-start-btn"
              className="shrink-0 flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-xl px-6 py-3.5 transition-all hover:-translate-y-0.5"
              style={{ background: accent }}
            >
              Start This Challenge <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">30-Day Curriculum</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="challenge-preview-day-grid">
            {preview.day_titles.map((title, i) => (
              <div
                key={i} data-testid={`challenge-preview-day-${i + 1}`}
                className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3.5 py-2.5"
              >
                <span className="text-[10px] font-bold shrink-0 w-14" style={{ color: accent }}>DAY {i + 1}</span>
                <span className="text-[12px] text-slate-300 leading-snug flex-1">{title}</span>
                {i > 0 && <Lock className="w-3 h-3 text-slate-600 shrink-0" />}
              </div>
            ))}
          </div>
          {!preview.isReal && (
            <p className="text-[11px] text-slate-600 mt-4">Illustrative curriculum preview — full real content unlocks as you progress after joining.</p>
          )}
          {preview.isCustomReal && (
            <p className="text-[11px] text-slate-600 mt-4">Real, AI-generated lessons and quizzes — content unlocks day by day as you complete each one.</p>
          )}
        </div>

        <div className="text-center mt-8">
          <button onClick={handleStart} data-testid="challenge-preview-start-btn-bottom" className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 px-7 py-3.5 rounded-xl transition-all hover:-translate-y-0.5">
            Start This Challenge <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </PublicPageShell>
  );
}
